import {
  inboxFilters,
  inboxGroupOptions,
  inboxSortOptions,
} from '@griply/utils';
import Typography from '@mui/material/Typography';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import {
  useInboxFilterDocument,
  useUpdateInboxFilterDocument,
} from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar18 } from 'shared/assets/icons';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { FilterButton } from 'shared/components/ui/filter-button';
import { IconButton } from 'shared/components/ui/icon-button';
import { MobileTodoNavigation } from 'shared/components/ui/mobile-todo-navigation';
import { FilterDocuments } from 'shared/constants';
import { useActivePath } from 'shared/hooks/use-active-path';
import { Paths } from 'shared/routes/paths';

import * as Styled from './page-top-bar.style';

export type PageTopBarProps = {
  onCalendar?: () => void;
  calendarOpen?: boolean;
};

export const PageTopBar: React.FC<PageTopBarProps> = ({
  onCalendar,
  calendarOpen,
}) => {
  const { t } = useTranslation();
  const activePath = useActivePath({ level: 2 });
  const inboxFilterDocument = useInboxFilterDocument();
  const updateInboxFilterDocument = useUpdateInboxFilterDocument();

  const goals = useSortedGoals();
  const tags = useSortedTags();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });

  const onReset = () =>
    updateInboxFilterDocument(FilterDocuments.Inbox.defaultFilterDocument);

  return (
    <TopBarBase
      mobileNavigationSlot={
        <MobileTodoNavigation activePath={activePath as Paths} />
      }
    >
      <Styled.Container>
        <Styled.Title>
          <Typography variant="h5" component="h1">
            {t('pages.inbox.title')}
          </Typography>
        </Styled.Title>
        <Styled.Interactions>
          {onCalendar && calendarOpen && (
            <IconButton
              icon={Calendar18}
              onClick={onCalendar}
              highlighted={calendarOpen}
              preventDrag
            />
          )}

          <FilterButton
            filterDocument={inboxFilterDocument}
            defaultFilterDocument={FilterDocuments.Inbox.defaultFilterDocument}
            updateFilterDocument={updateInboxFilterDocument}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            supportedGroupByModes={inboxGroupOptions}
            supportedSortingModes={inboxSortOptions}
            supportedFilters={inboxFilters}
            onReset={onReset}
            preventDrag
            renderAsIconButton
          />
        </Styled.Interactions>
      </Styled.Container>
    </TopBarBase>
  );
};
